import { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../App";
import { ArrowLeft, Calendar, TrendingUp, Trophy, Target, ChevronDown, Building2 } from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

const DEPARTMENTS = {
  sales: { label: "Sales", color: "bg-indigo-600", hex: "#6366F1" },
  csm: { label: "Customer Success", color: "bg-emerald-600", hex: "#10B981" },
  dev: { label: "Development", color: "bg-purple-600", hex: "#8B5CF6" },
  support: { label: "Support", color: "bg-amber-600", hex: "#F59E0B" },
  marketing: { label: "Marketing", color: "bg-pink-600", hex: "#EC4899" },
};

const DEPT_METRICS = {
  sales: [
    { key: "team_generated", label: "Team Gen" },
    { key: "self_generated", label: "Self Gen" },
    { key: "demos_held", label: "Demos" },
    { key: "upgrades_closed", label: "Upgrades" },
    { key: "new_deals_closed", label: "Deals" },
  ],
  csm: [
    { key: "renewals", label: "Renewals" },
    { key: "upsells", label: "Upsells" },
    { key: "health_checks", label: "Health Checks" },
    { key: "nps_score", label: "NPS Score" },
    { key: "churn_prevention", label: "Churn Prevented" },
  ],
  dev: [
    { key: "features_shipped", label: "Features" },
    { key: "bugs_fixed", label: "Bugs Fixed" },
    { key: "code_reviews", label: "Code Reviews" },
    { key: "story_points", label: "Story Points" },
    { key: "deployments", label: "Deployments" },
  ],
  support: [
    { key: "tickets_resolved", label: "Tickets" },
    { key: "avg_response_time", label: "Resp Time" },
    { key: "csat_score", label: "CSAT" },
    { key: "escalations_handled", label: "Escalations" },
    { key: "kb_articles", label: "KB Articles" },
  ],
  marketing: [
    { key: "leads_generated", label: "Leads" },
    { key: "campaigns_launched", label: "Campaigns" },
    { key: "content_pieces", label: "Content" },
    { key: "social_engagement", label: "Social" },
    { key: "email_opens", label: "Email Opens" },
  ],
};

const ScorecardDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();
  const [salesperson, setSalesperson] = useState(null);
  const [entries, setEntries] = useState([]);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [loading, setLoading] = useState(true);

  const headers = { Authorization: `Bearer ${token}` };

  const department = salesperson?.department || "sales";
  const deptConfig = DEPARTMENTS[department] || DEPARTMENTS.sales;
  const METRICS = DEPT_METRICS[department] || DEPT_METRICS.sales;

  useEffect(() => {
    fetchData();
  }, [id, selectedYear, selectedMonth]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [personRes, entriesRes] = await Promise.all([
        axios.get(`${API}/salespeople/${id}`, { headers }),
        axios.get(`${API}/entries?salesperson_id=${id}&year=${selectedYear}&month=${selectedMonth}`, { headers }),
      ]);
      setSalesperson(personRes.data);
      setEntries(entriesRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Generate week ending dates for selected month
  const weekEndings = useMemo(() => {
    const weeks = [];
    const year = selectedYear;
    const month = selectedMonth - 1;
    
    let date = new Date(year, month, 1);
    const dayOfWeek = date.getDay();
    const daysUntilFriday = (5 - dayOfWeek + 7) % 7;
    date.setDate(date.getDate() + daysUntilFriday);
    
    if (date.getMonth() !== month) {
      date.setDate(date.getDate() + 7);
    }
    
    while (date.getMonth() === month) {
      weeks.push(new Date(date));
      date.setDate(date.getDate() + 7);
    }
    
    if (weeks.length < 4 && weeks.length > 0) {
      const lastFriday = new Date(weeks[weeks.length - 1]);
      lastFriday.setDate(lastFriday.getDate() + 7);
      if (weeks.length < 5) {
        weeks.push(lastFriday);
      }
    }
    
    return weeks.slice(0, 5);
  }, [selectedYear, selectedMonth]);

  const entriesByWeek = useMemo(() => {
    const map = {};
    entries.forEach((entry) => {
      map[entry.week_ending_date] = entry;
    });
    return map;
  }, [entries]);

  const totals = useMemo(() => {
    const result = {};
    METRICS.forEach(m => { result[m.key] = 0; });
    entries.forEach((entry) => {
      METRICS.forEach(m => {
        result[m.key] += entry.metrics[m.key] || 0;
      });
    });
    return result;
  }, [entries, METRICS]);

  const weeklyTotals = useMemo(() => {
    return weekEndings.map((weekDate) => {
      const dateStr = weekDate.toISOString().split("T")[0];
      const entry = entriesByWeek[dateStr];
      if (!entry) return 0;
      return METRICS.reduce((sum, m) => sum + (entry.metrics[m.key] || 0), 0);
    });
  }, [weekEndings, entriesByWeek, METRICS]);

  const chartData = weekEndings.map((weekDate, index) => ({
    name: `Wk ${index + 1}`,
    fullName: `${MONTHS[weekDate.getMonth()].substring(0, 3)} ${weekDate.getDate()}`,
    value: weeklyTotals[index],
  }));

  const monthlyChartData = METRICS.map((metric) => ({
    name: metric.label,
    value: totals[metric.key] || 0,
  }));

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  if (!salesperson) {
    return (
      <div className="text-center py-12 bg-slate-800/50 rounded-lg border border-slate-700/50">
        <p className="text-slate-400">Team member not found</p>
        <button
          onClick={() => navigate("/scorecards")}
          className="mt-4 text-indigo-400 hover:text-indigo-300"
        >
          Back to Cards
        </button>
      </div>
    );
  }

  const formatDate = (date) => {
    return `${MONTHS[date.getMonth()].substring(0, 3)} ${date.getDate()}`;
  };

  const grandTotal = Object.values(totals).reduce((a, b) => a + b, 0);

  return (
    <div className="animate-fade-in" data-testid="scorecard-detail">
      {/* Header */}
      <div className="mb-4">
        <button
          onClick={() => navigate("/scorecards")}
          className="flex items-center gap-2 text-slate-400 hover:text-slate-200 mb-3 transition-colors text-sm"
          data-testid="back-button"
        >
          <ArrowLeft size={16} />
          <span>Back to Cards</span>
        </button>
        
        <div className="bg-slate-800/50 rounded-lg border border-slate-700/50 p-4">
          {/* Top row: Avatar and Name */}
          <div className="flex items-center gap-3 mb-3">
            {/* Avatar */}
            <div className={`w-12 h-12 ${deptConfig.color} rounded-xl flex items-center justify-center text-white font-bold text-lg flex-shrink-0`}>
              {salesperson.name.charAt(0)}
            </div>
            
            <div className="flex-1 min-w-0">
              <h1 className="text-base font-bold text-slate-100 truncate" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {salesperson.name}
              </h1>
              <span className={`inline-block text-[10px] px-2 py-0.5 rounded ${deptConfig.color} text-white mt-1`}>
                {deptConfig.label}
              </span>
            </div>
          </div>
          
          {/* Month/Year Selectors - separate row */}
          <div className="flex items-center gap-2 mb-4">
            <Select value={String(selectedMonth)} onValueChange={(v) => setSelectedMonth(Number(v))}>
              <SelectTrigger className="w-[90px] h-8 text-xs bg-slate-900 border-slate-700 text-slate-200">
                <Calendar size={12} className="mr-1" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-700">
                {MONTHS.map((month, index) => (
                  <SelectItem key={month} value={String(index + 1)} className="text-slate-200 text-xs">
                    {month.substring(0, 3)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={String(selectedYear)} onValueChange={(v) => setSelectedYear(Number(v))}>
              <SelectTrigger className="w-[70px] h-8 text-xs bg-slate-900 border-slate-700 text-slate-200">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-slate-800 border-slate-700">
                {[2024, 2025, 2026, 2027].map((year) => (
                  <SelectItem key={year} value={String(year)} className="text-slate-200 text-xs">
                    {year}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {/* Summary Stats */}
          <div className="grid grid-cols-3 gap-3 pt-3 border-t border-slate-700/50">
            <div className="text-center">
              <div className="text-2xl font-bold text-white">{grandTotal.toFixed(0)}</div>
              <div className="text-[10px] text-slate-500 uppercase">Total Points</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold" style={{ color: deptConfig.hex }}>
                {totals[METRICS[METRICS.length - 1]?.key]?.toFixed(0) || 0}
              </div>
              <div className="text-[10px] text-slate-500 uppercase">{METRICS[METRICS.length - 1]?.label}</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-slate-300">{entries.length}</div>
              <div className="text-[10px] text-slate-500 uppercase">Weeks Logged</div>
            </div>
          </div>
        </div>
      </div>

      {/* Weekly Breakdown Table */}
      <div className="bg-slate-800/50 rounded-lg border border-slate-700/50 mb-4 overflow-hidden">
        <div className="p-3 border-b border-slate-700/50">
          <h3 className="text-sm font-semibold text-slate-300 flex items-center gap-2">
            <Target size={14} />
            Weekly Breakdown
          </h3>
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="scorecard-table">
            <thead>
              <tr className="bg-slate-900/50">
                <th className="text-left py-2 px-3 text-slate-400 font-medium min-w-[100px]">Metric</th>
                {weekEndings.map((weekDate, index) => (
                  <th key={index} className="text-center py-2 px-2 text-slate-400 font-medium min-w-[60px]">
                    <div className="text-[10px]">{formatDate(weekDate)}</div>
                  </th>
                ))}
                <th className="text-right py-2 px-3 text-slate-300 font-semibold min-w-[60px]">Total</th>
              </tr>
            </thead>
            <tbody>
              {METRICS.map((metric, idx) => (
                <tr 
                  key={metric.key} 
                  className={`border-t border-slate-700/30 ${idx % 2 === 0 ? 'bg-slate-900/20' : ''}`}
                  data-testid={`metric-row-${metric.key}`}
                >
                  <td className="py-2 px-3 text-slate-300 font-medium">{metric.label}</td>
                  {weekEndings.map((weekDate, index) => {
                    const dateStr = weekDate.toISOString().split("T")[0];
                    const entry = entriesByWeek[dateStr];
                    const value = entry?.metrics[metric.key] || 0;
                    return (
                      <td key={index} className="text-center py-2 px-2 text-slate-400">
                        {value > 0 ? value.toFixed(0) : '-'}
                      </td>
                    );
                  })}
                  <td className="text-right py-2 px-3 font-semibold" style={{ color: deptConfig.hex }}>
                    {(totals[metric.key] || 0).toFixed(0)}
                  </td>
                </tr>
              ))}
              {/* Weekly Totals Row */}
              <tr className="border-t-2 border-slate-600 bg-slate-900/50">
                <td className="py-2 px-3 font-semibold text-slate-200">Week Total</td>
                {weeklyTotals.map((total, index) => (
                  <td key={index} className="text-center py-2 px-2 font-semibold text-slate-300">
                    {total > 0 ? total.toFixed(0) : '-'}
                  </td>
                ))}
                <td className="text-right py-2 px-3 font-bold text-lg text-white">
                  {grandTotal.toFixed(0)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Weekly Trend Chart */}
        <div className="bg-slate-800/50 rounded-lg border border-slate-700/50 p-4">
          <h3 className="text-sm font-semibold text-slate-300 mb-3 flex items-center gap-2">
            <TrendingUp size={14} />
            Weekly Trend
          </h3>
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={chartData} margin={{ left: -10, right: 10, top: 10, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#334155" vertical={false} />
              <XAxis 
                dataKey="name" 
                tick={{ fill: '#94a3b8', fontSize: 10 }} 
                axisLine={{ stroke: '#475569' }}
                tickLine={false}
              />
              <YAxis 
                tick={{ fill: '#94a3b8', fontSize: 10 }} 
                axisLine={{ stroke: '#475569' }}
                tickLine={false}
              />
              <Tooltip
                contentStyle={{
                  background: '#1e293b',
                  border: '1px solid #334155',
                  borderRadius: '8px',
                  fontSize: '11px',
                }}
                labelStyle={{ color: '#f1f5f9' }}
                itemStyle={{ color: '#f1f5f9' }}
                cursor={false}
              />
              <Bar dataKey="value" radius={[4, 4, 0, 0]} fill={deptConfig.hex} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Metrics Breakdown */}
        <div className="bg-slate-800/50 rounded-lg border border-slate-700/50 p-4">
          <h3 className="text-sm font-semibold text-slate-300 mb-3 flex items-center gap-2">
            <Trophy size={14} />
            Metrics Breakdown
          </h3>
          <div className="space-y-2">
            {METRICS.map((metric, index) => {
              const value = totals[metric.key] || 0;
              const maxValue = Math.max(...Object.values(totals), 1);
              const percent = (value / maxValue) * 100;
              
              return (
                <div key={metric.key} className="flex items-center gap-3">
                  <div className="w-20 text-xs text-slate-400 truncate">{metric.label}</div>
                  <div className="flex-1 h-2 bg-slate-700/50 rounded-full overflow-hidden">
                    <div 
                      className="h-full rounded-full transition-all"
                      style={{ 
                        width: `${percent}%`,
                        backgroundColor: deptConfig.hex,
                        opacity: 0.5 + (index * 0.1)
                      }}
                    />
                  </div>
                  <div className="w-12 text-right text-xs font-medium text-slate-300">
                    {value.toFixed(0)}
                  </div>
                </div>
              );
            })}
          </div>
          
          {/* Total */}
          <div className="mt-4 pt-3 border-t border-slate-700/50 flex justify-between items-center">
            <span className="text-xs text-slate-500 uppercase">Month Total</span>
            <span className="text-xl font-bold text-white">{grandTotal.toFixed(0)}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ScorecardDetail;
